import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Upload, CheckCircle2, AlertCircle, FileImage, FileText, Film, Loader2, X } from "lucide-react";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

export default function DesignerUploadPortal() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [caption, setCaption] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    let active = true;
    fetch(`${API}/content-studio/upload-portal/${token}`)
      .then(r => r.json().then(d => ({ ok: r.ok, data: d, status: r.status })))
      .then(({ ok, data }) => {
        if (!active) return;
        if (!ok) {
          setError(data.detail || "Could not load upload context");
        } else {
          setCtx(data);
          setUploaded(data.existing_assets || []);
        }
      })
      .catch(e => { if (active) setError("Network error: " + e.message); });
    return () => { active = false; };
  }, [token]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (caption.trim()) fd.append("caption", caption.trim());
    const r = await fetch(`${API}/content-studio/upload-portal/${token}`, { method: "POST", body: fd });
    setUploading(false);
    if (r.ok) {
      const data = await r.json();
      setUploaded(prev => [data, ...prev]);
      setCaption("");
    } else {
      const e = await r.json().catch(() => ({}));
      alert(e.detail || "Upload failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    files.forEach(f => doUpload(f));
  };

  if (error) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" }}>
        <div style={{ background: "white", borderRadius: 16, padding: "48px 32px", maxWidth: 440, textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
          <AlertCircle style={{ width: 56, height: 56, color: "#ef4444", margin: "0 auto 16px" }} />
          <h1 style={{ fontSize: 22, color: "#1f2937", margin: "0 0 8px" }}>Link Unavailable</h1>
          <p style={{ color: "#6b7280", margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!ctx) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb" }}>
        <Loader2 className="animate-spin" style={{ width: 32, height: 32, color: "#4f46e5" }} />
      </div>
    );
  }

  const card = ctx.card || {};
  const isPersonal = card.post_type === "personal";

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", padding: "32px 16px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "white", borderRadius: "12px 12px 0 0", padding: 24 }}>
          <p style={{ margin: 0, fontSize: 11, letterSpacing: 1.5, opacity: 0.85 }}>REX CONTENT STUDIO</p>
          <h1 style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600 }}>Upload your finished artwork</h1>
          <p style={{ margin: "8px 0 0", fontSize: 13, opacity: 0.85 }}>Drop carousel slides, quote cards, or any graphics. The post auto-moves to <strong>In Review</strong> after first upload.</p>
        </div>

        {/* Card brief */}
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderTop: "none", padding: 24 }} data-testid="card-brief">
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <span style={{ background: isPersonal ? "#ede9fe" : "#dbeafe", color: isPersonal ? "#6d28d9" : "#1d4ed8", padding: "2px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
              {isPersonal ? "Personal / Founder" : "RESILIGENT"}
            </span>
            {card.client_name && <span style={{ color: "#6b7280", fontSize: 12 }}>• {card.client_name}</span>}
            {card.due_date && <span style={{ color: "#dc2626", fontSize: 12, marginLeft: "auto" }}>Due {card.due_date}</span>}
          </div>
          <h2 style={{ margin: "8px 0 8px", fontSize: 18, color: "#1f2937" }}>{card.title || "Untitled card"}</h2>
          {card.description && <p style={{ color: "#4b5563", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-line", margin: "8px 0 0" }}>{card.description}</p>}
          {card.generated_content && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Show caption / post copy</summary>
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, marginTop: 8, whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.6, color: "#374151" }}>
                {card.generated_content}
              </div>
            </details>
          )}
        </div>

        {/* Upload zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          style={{
            background: dragOver ? "#eef2ff" : "white",
            border: `2px dashed ${dragOver ? "#4f46e5" : "#d1d5db"}`,
            borderTop: "none",
            padding: 32,
            textAlign: "center",
            transition: "all 0.15s",
          }}
          data-testid="upload-zone"
        >
          <Upload style={{ width: 40, height: 40, color: dragOver ? "#4f46e5" : "#9ca3af", margin: "0 auto 12px" }} />
          <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600, color: "#1f2937" }}>
            {dragOver ? "Drop files to upload" : "Drag & drop your finished artwork"}
          </p>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#6b7280" }}>PNG · JPG · PDF · MP4 · max 25MB · {ctx.remaining_uploads} uploads remaining</p>
          <input type="text" placeholder="Optional caption / slide title" value={caption} onChange={e => setCaption(e.target.value)}
            style={{ padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, width: "100%", maxWidth: 360, marginBottom: 12 }} />
          <div>
            <input type="file" ref={fileRef} style={{ display: "none" }} accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.mp4,.mov" onChange={e => e.target.files?.[0] && doUpload(e.target.files[0])} data-testid="file-input"/>
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              style={{ background: "#4f46e5", color: "white", padding: "10px 24px", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
              data-testid="choose-file-btn">
              {uploading ? <><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Uploading...</> : "Choose File"}
            </button>
          </div>
        </div>

        {/* Uploaded assets */}
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 12px 12px", padding: 24 }}>
          <h3 style={{ fontSize: 14, color: "#4f46e5", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 12px" }}>
            Uploaded ({uploaded.length})
          </h3>
          {uploaded.length === 0 ? (
            <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: "16px 0" }}>No files uploaded yet</p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }} data-testid="uploaded-assets">
              {uploaded.map(a => (
                <div key={a.asset_id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, fontSize: 12 }} data-testid={`asset-${a.asset_id}`}>
                  <AssetIcon mime={a.mime_type} />
                  <p style={{ margin: "6px 0 2px", fontWeight: 600, color: "#1f2937", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.filename}>{a.filename}</p>
                  <p style={{ margin: 0, fontSize: 10, color: "#6b7280" }}>{a.asset_type || "graphic"}</p>
                  <CheckCircle2 style={{ width: 14, height: 14, color: "#10b981", marginTop: 6 }} />
                </div>
              ))}
            </div>
          )}
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: "#9ca3af", marginTop: 16 }}>
          This is a one-time upload link. Link expires {ctx.expires_at ? new Date(ctx.expires_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "soon"}.
        </p>
      </div>
    </div>
  );
}

function AssetIcon({ mime }) {
  if (mime?.startsWith("image/")) return <FileImage style={{ width: 28, height: 28, color: "#6366f1" }} />;
  if (mime?.startsWith("video/")) return <Film style={{ width: 28, height: 28, color: "#ec4899" }} />;
  return <FileText style={{ width: 28, height: 28, color: "#94a3b8" }} />;
}
